
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Upload, Download } from 'lucide-react';
import { FileUploadSection } from './batch-upload/FileUploadSection';
import { FormatRequirements } from './batch-upload/FormatRequirements';
import { useFlightUpload } from '@/hooks/useFlightUpload';

export function BatchFlightUpload() {
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [uploadResult, setUploadResult] = useState<any>(null);
  const { toast } = useToast();
  const { uploading, uploadFlights } = useFlightUpload();

  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setFile(selectedFile);
      setUploadProgress('');
    } else {
      setFile(null);
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a CSV file.",
      });
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast({
        variant: "destructive",
        title: "No file selected",
        description: "Please select a CSV file to upload.",
      });
      return;
    }

    setUploadProgress('Starting upload...');
    setUploadResult(null);
    console.log('🚀 Starting flight schedule upload');
    
    const result = await uploadFlights(file);
    setUploadResult(result);
    
    if (result.success) {
      setFile(null);
      setUploadProgress('Upload completed!');
      // Reset file input
      const fileInput = document.getElementById('csv-file') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
      console.log('✅ Flight schedule upload completed');
      console.log('📊 Navigate to Flight Board to verify the new flights are displayed');
    } else {
      setUploadProgress('Upload failed.');
      console.log('❌ Flight schedule upload failed');
    }
  };

  const downloadSampleCSV = () => {
    // Create a sample based on the PNG Air data format
    const sampleData = `flight_number,organization_code,departure_city_code,arrival_city_code,aircraft_type_code,scheduled_departure,scheduled_arrival,status,status_reason,gate
CG8604,ANG,POM,LAE,DH8A,2025-09-05T07:00:00+00:00,2025-09-05T08:10:00+00:00,On Time,,1
CG8546,ANG,POM,HGU,DH8A,2025-09-05T08:50:00+00:00,2025-09-05T10:25:00+00:00,On Time,,1`;
    
    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sample-flight-schedule.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Card>
      <CardHeader style={{ backgroundColor: 'orange' }}>
        <CardTitle className="flex items-center gap-2">
          <Upload className="h-5 w-5" />
          Flight Schedule Upload
        </CardTitle>
        <CardDescription>
          Upload the CSV for flight data
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormatRequirements />
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={downloadSampleCSV}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Download Sample CSV Format
          </Button>
        </div>
        
        <FileUploadSection 
          file={file} 
          onFileChange={handleFileChange} 
        />
        
        {uploadProgress && (
          <div className="p-3 border rounded-md bg-blue-50 dark:bg-blue-950/20">
            <p className="text-sm text-blue-800 dark:text-blue-200">{uploadProgress}</p>
          </div>
        )}
        
        {uploadResult && (
          <div className="space-y-4 p-4 border rounded-lg bg-card">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-lg">Upload Results</h3>
            </div>
            
            {/* Summary Statistics */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-muted rounded-lg">
                <p className="text-2xl font-bold">{uploadResult.totalFlights}</p>
                <p className="text-xs text-muted-foreground">Total Flights</p>
              </div>
              <div className="text-center p-3 bg-green-500/10 rounded-lg">
                <p className="text-2xl font-bold text-green-600 dark:text-green-500">{uploadResult.successCount}</p>
                <p className="text-xs text-muted-foreground">✓ Successful</p>
              </div>
              <div className="text-center p-3 bg-destructive/10 rounded-lg">
                <p className="text-2xl font-bold text-destructive">{uploadResult.errorCount}</p>
                <p className="text-xs text-muted-foreground">✗ Failed</p>
              </div>
            </div>

            {/* Failed Flights List */}
            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-destructive">Failed Flights:</h4>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {uploadResult.errors.map((error: any, index: number) => (
                    <div key={index} className="text-sm p-3 bg-destructive/5 border border-destructive/20 rounded-md">
                      <div className="flex justify-between items-start gap-2">
                        <span className="font-semibold text-destructive">Flight {error.flightNumber}</span>
                        <span className="text-xs text-muted-foreground">Row {error.row}</span>
                      </div>
                      <p className="text-muted-foreground mt-1">{error.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadResult.successCount > 0 && (
              <div className="text-sm text-green-600 dark:text-green-500">
                ✓ Successfully uploaded {uploadResult.successCount} flight{uploadResult.successCount !== 1 ? 's' : ''}
              </div>
            )}
          </div>
        )}
        
        <Button 
          onClick={handleUpload} 
          disabled={!file || uploading}
          className="w-full"
        >
          {uploading ? "Uploading PNG Air Flights..." : "Upload Schedule"}
        </Button>
        
        {!uploading && file && (
          <div className="text-sm text-muted-foreground">
            Ready to upload {file.name}. This will add PNG Air flights for September 5-10, 2025.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
